<template>
  <div
    class="link-danger"
    :data-key="columnKey"
  >
    <div class="d-flex justify-content-between align-items-start">
      <a
        href="#"
        class="link-danger fw-bolder fs-7"
        @click.prevent="sortFn(columnId)"
        v-if="!modalstack && filter"
      >
        <slot />
        <i
          v-if="listModel.order_field === columnId"
          :class="{
            bi: true,
            'bi-arrow-up': listModel.order_direction == 'DESC',
            'bi-arrow-down': listModel.order_direction == 'ASC',
          }"
        />
      </a>
      <span
        v-else
        class="fw-bolder fs-7"
      >
        <slot />
      </span>

      <a
        href="#"
        class="link-danger fw-bolder fs-7"
        @click.prevent="toggleFilter"
        v-if="!modalstack && filter"
      >
        <i class="bi bi-filter" />
      </a>
    </div>
    <div
      v-if="isFilterOpen"
      class="filter-dropdown d-block mt-2"
    >
      <div
        v-if="options.length > 0"
        class="d-flex flex-row mb-3"
      >
        <MultiSelectDropdown
          v-model="selectedOptions"
          :options="options"
        />
      </div>
      <div
        v-else-if="isDateColumn"
        class="d-flex flex-row mb-3 filter-datepicker"
      >
        <Datepicker
          v-model="dmod"
          teleport
          range
        />
      </div>
      <div
        v-else-if="isBool"
        class="d-flex flex-row mb-3"
      >
        <select
          class="form-select me-2"
          v-model="selectedBool"
        >
          <option
            v-for="opt in boolOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>
      <div
        v-else
        class="d-flex flex-row mb-3"
      >
        <input
          class="form-control"
          :placeholder="t('common.filter')"
          v-model="searchTerm"
        />
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, inject, nextTick, ref, shallowRef, toValue, watch } from 'vue'
import { watchPausable, watchThrottled } from '@vueuse/core'
import { format, isValid } from 'date-fns'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useActivityStore } from '@/stores/activity'
import { parseDateInput } from '@/lib/dateInput.js'
import MultiSelectDropdown from '@/components/dropdowns/msdd.vue'
import Datepicker from '@/components/datepicker.vue'
import { dropdownLabel, namedEntityLabel } from '@/lib/dropdownLabel.js'

const { t, locale } = useI18n()

const props = defineProps({
  columnId: String,
  width: String,
  columnKey: String,
  headless: Boolean,
  isBool: Boolean,
  filter: {
    type: Boolean,
    default: true,
  },
  filterOptions: {
    type: Array,
    default: null,
  },
})
const sortFn = inject('sort-fn')
const listModel = inject('list-model')
const modelVSet = inject('model-valueset')
const modalstack = inject('modalstack', false)
const cfg = inject('th-cfg', undefined)
const setColumnWidthForFilter = inject('set-column-width-for-filter', null)

const { activityTypes } = storeToRefs(useActivityStore())

const options = ref([])
const searchTerm = shallowRef('')
const isFilterOpen = ref(false)
const selectedOptions = ref([])
const selectedBool = ref('')
const dmod = ref()

const MIN_DATE_FILTER_COL_WIDTH = 220
const isDateColumn = computed(
  () =>
    props.columnId.includes('date') ||
    props.columnId.includes('till') ||
    props.columnId === 'archived_at'
)

let filter_term = ''
const dateFormat = 'dd.MM.y'
const boolOptions = computed(() => [
  { value: '', label: t('common.all') },
  { value: '1', label: t('common.yes') },
  { value: '0', label: t('common.no') },
])

function toFilterDate(d) {
  if (d instanceof Date && isValid(d)) return d
  if (typeof d === 'string') return parseDateInput(d)
  return null
}

function formatFilterDate(d) {
  const date = toFilterDate(d)
  if (!date) return ''
  try {
    return format(date, dateFormat)
  } catch {
    return ''
  }
}

function parsePeriodFilterValue(raw) {
  const s = String(raw ?? '').trim()
  const m = s.match(/^(\d{1,2}\.\d{1,2}\.\d{2,4})?-(\d{1,2}\.\d{1,2}\.\d{2,4})?$/)
  if (m) {
    const from = m[1] ? parseDateInput(m[1]) : null
    const to = m[2] ? parseDateInput(m[2]) : null
    if (from && to) return [from, to]
    if (from) return [from, from]
    if (to) return [to, to]
    return null
  }
  const parts = s.split('-').map(p => p.trim()).filter(Boolean)
  if (parts.length >= 2) {
    const from = parseDateInput(parts[0])
    const to = parseDateInput(parts[1])
    if (from && to) return [from, to]
  }
  if (parts.length === 1) {
    const d = parseDateInput(parts[0])
    return d ? [d, d] : null
  }
  return null
}

const { pause: filterDropdownPause, resume: filterDropdownResume } = watchPausable(
  selectedOptions,
  newSelectedOptions => {
    let filter_field = props.columnId
    const selectedLabels = options.value
      .filter(option => newSelectedOptions.includes(option.value))
      .map(option => option.label)
      .join('[..]')

    const cfgData = cfg && typeof cfg === 'object' && 'value' in cfg ? cfg.value : cfg
    if (cfgData?.[`io_${props.columnId}`]) {
      filter_term = options.value
        .filter(option => newSelectedOptions.includes(option.value))
        .map(option => option.value)
        .join('[..]')
      const nodes = props.columnId.split('.')
      if (nodes.length > 1 && nodes[1] !== 'id') {
        filter_field = nodes[0]
      }
    } else if (filter_field.includes('activity_subtype.parent_name')) {
      filter_term = options.value
        .filter(option => newSelectedOptions.includes(option.value))
        .map(option => option.value)
        .join('[..]')
      filter_field = filter_field.replace('parent_name', 'parent_type')
    } else if (filter_field === 'entity_type') {
      // Для entity_type отправляем value (id), а не label (русский текст)
      filter_term = options.value
        .filter(option => newSelectedOptions.includes(option.value))
        .map(option => option.value)
        .join('[..]')
    } else {
      filter_term = selectedLabels
    }
    modelVSet({ page: 1, filter: filter_field, filter_id: filter_term })
  }
)

const { pause: filterDatePause, resume: filterDateResume } = watchPausable(dmod, value => {
  if (value == null) {
    modelVSet({ page: 1, filter: props.columnId, filter_id: '' })
    return
  }

  if (!Array.isArray(value)) {
    return
  }

  const from = formatFilterDate(value[0])
  const to = formatFilterDate(value[1] ?? value[0])
  if (!from || !to) {
    return
  }

  modelVSet({
    page: 1,
    filter: props.columnId,
    filter_id: `${from}-${to}`,
  })
})

const { pause: filterBoolPause, resume: filterBoolResume } = watchPausable(selectedBool, newVal => {
  filter_term = newVal === '' ? '' : newVal
  modelVSet({ page: 1, filter: props.columnId, filter_id: filter_term })
})

const filters = computed(() => {
  const lm = toValue(listModel)

  const keys = String(lm.filter).split('&&')
  const vals = String(lm.filter_id).split('&&')

  let result = {}
  for (let i in keys) {
    let key = keys[i]
    let val = vals[i]

    key = key.replace('parent_type', 'parent_name')

    if (!Array.isArray(result[key])) {
      result[key] = []
    }

    const _vals = val.split('[..]')
    result[key] = [...result[key], ..._vals]
  }

  return result
})

watch(
  [options, filters, () => props.columnId, isDateColumn, () => props.isBool],
  ([_options, _filters, _columnId, _isDateColumn, _isBool]) => {
    let filter = _filters[_columnId]
    if (!filter) {
      const cfgData = toValue(cfg)
      if (cfgData?.[`io_${_columnId}`]) {
        const nodes = _columnId.split('.')
        if (nodes.length > 1 && nodes[1] !== 'id') {
          filter = _filters[nodes[0]]
        }
      }
    }
    if (!filter) {
      if (_isDateColumn && dmod.value != null) {
        filterDatePause()
        dmod.value = null
        nextTick(filterDateResume)
      }
      return
    }

    isFilterOpen.value = true

    if (_options.length) {
      filterDropdownPause()
      selectedOptions.value = filter
        .map(fv => _options.find(ov => [ov.value, ov.label].includes(fv)))
        .filter(Boolean)
        .map(({ value }) => value)
      nextTick(filterDropdownResume)
      return
    }

    if (_isDateColumn) {
      filterDatePause()
      dmod.value = parsePeriodFilterValue(filter[0] || '')
      nextTick(filterDateResume)
      return
    }

    if (_isBool) {
      filterBoolPause()
      selectedBool.value = filter[0]
      nextTick(filterBoolResume)
    }

    if (!filter.length) {
      return
    }

    searchTerm.value = filter[0]
  },
  {
    immediate: true,
    deep: true,
  }
)

watch(
  [() => props.columnId, () => props.filterOptions, activityTypes, cfg, filters, locale],
  ([columnId, filterOptions, activityTypes, cfg]) => {
    if (!columnId) {
      return
    }

    const cfgData = toValue(cfg)
    const nodes = columnId.split('.')
    if (filterOptions?.length) {
      options.value = filterOptions.map(item => ({
        value: String(item.id),
        label: dropdownLabel(item) || String(item.id),
      }))

      return
    }

    if (activityTypes.length && columnId.includes('activity_')) {
      const isSubtypeCol = columnId.includes('subtype.name')
      const currentFilters = filters.value
      const findType = v =>
        activityTypes.find(at => String(at.id) === String(v) || at.name === v)

      const selectedParentIds = new Set(
        (currentFilters['activity.activity_subtype.parent_name'] || [])
          .map(findType)
          .filter(Boolean)
          .map(at => at.id)
      )
      const selectedSubtypeParentIds = new Set(
        (currentFilters['activity.activity_subtype.name'] || [])
          .map(findType)
          .filter(Boolean)
          .map(at => at.parent_id)
          .filter(pid => pid != null)
      )

      options.value = activityTypes
        .filter(at => {
          if (isSubtypeCol) {
            if (at.parent_id == null) return false
            return !selectedParentIds.size || selectedParentIds.has(at.parent_id)
          }
          if (at.parent_id != null) return false
          return !selectedSubtypeParentIds.size || selectedSubtypeParentIds.has(at.id)
        })
        .map(item => ({
          value: String(item.id),
          label: namedEntityLabel(item) || (item.name ? String(item.name) : `ID ${item.id}`),
        }))

      return
    }

    if (!cfgData) {
      return
    }

    for (const node of nodes) {
      const arr = cfgData[node]

      if (!Array.isArray(arr)) {
        continue
      }

      options.value = arr
        .filter(item => item != null)
        .map(item => ({
          value: String(item.id),
          label: dropdownLabel(item) || `ID: ${item.id}`,
        }))
    }
  },
  { immediate: true, deep: true }
)

watchThrottled(
  searchTerm,
  (filter_id, osearch) => {
    if (filter_id === undefined && osearch === '') {
      return
    }
    filter_term = filter_id
    modelVSet({ page: 1, filter: props.columnId, filter_id })
  },
  { throttle: 500 }
)

watch(
  [isFilterOpen, isDateColumn, () => props.columnKey],
  ([open, isDateColumn, columnKey]) => {
    if (!setColumnWidthForFilter || !isDateColumn) {
      return
    }

    setColumnWidthForFilter(columnKey, open ? MIN_DATE_FILTER_COL_WIDTH : null)
  },
  { immediate: true }
)

function toggleFilter() {
  if (isFilterOpen.value) {
    dmod.value = null
    searchTerm.value = ''
    selectedOptions.value = []
    selectedBool.value = ''
    modelVSet({ page: 1, filter: props.columnId, filter_id: '' })
  }
  isFilterOpen.value = !isFilterOpen.value
}
</script>
<style scoped>
.form-select {
  font-size: inherit;
}
.d-flex.justify-content-between > a:last-child {
  flex-shrink: 0;
  margin-left: 0.25rem;
}
</style>
